import { createAction, createReducer } from "@reduxjs/toolkit";
import { SubmissionSlotData } from "types/SubmissionSlots";

import { STORES } from "store/types";

import { SurveyData, ValidationStatus } from "./types";

export interface SubmissionSlotDetailsState {
  currentSubmissionSlotData: SubmissionSlotData | null;
  enableLoading: boolean;
  pristine: boolean;
  isSubmitVisible: boolean;
  validationStatus: ValidationStatus;
  dataTaskPreviousState: boolean;
  status?: string;
}

// define action types
export const initialize = createAction(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/initialize`
);

export const setCurrentSubmissionSlotData =
  createAction<SubmissionSlotData | null>(
    `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setCurrentSubmissionSlotData`
  );

export const setEnableLoading = createAction<boolean>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setEnableLoading`
);

export const setPristine = createAction<boolean>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setPristine`
);

export const setIsSubmitVisible = createAction<boolean>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setIsSubmitVisible`
);

export const setIsSaveVisible = createAction<boolean>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setIsSaveVisible`
);

export const setValidationStatus = createAction<ValidationStatus>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setValidationStatus`
);

export const setSurveyData = createAction<SurveyData>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setSurveyData`
);

export const setStatus = createAction<string | undefined>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setStatus`
);

export const setDataTaskPreviousState = createAction<boolean>(
  `${STORES.dataCollection}/${STORES.submissionSlotDetails}/setDataTaskPreviousState`
);

// create reducer
const submissionSlotDetailsReducer = createReducer<SubmissionSlotDetailsState>(
  {
    currentSubmissionSlotData: null,
    enableLoading: false,
    pristine: true,
    isSubmitVisible: true,
    validationStatus: ValidationStatus.READY,
    status: undefined,
    dataTaskPreviousState: false,
  },

  (builder) => {
    builder.addCase(initialize, (state) => initializeState(state));

    builder.addCase(setCurrentSubmissionSlotData, (state, action) => {
      state.currentSubmissionSlotData = action.payload;
    });

    builder.addCase(setEnableLoading, (state, action) => {
      state.enableLoading = action.payload;
    });

    builder.addCase(setPristine, (state, action) => {
      state.pristine = action.payload;
    });

    builder.addCase(setIsSubmitVisible, (state, action) => {
      state.isSubmitVisible = action.payload;
    });

    builder.addCase(setValidationStatus, (state, action) => {
      state.validationStatus = action.payload;
    });

    builder.addCase(setSurveyData, (state, action) => {
      const { tabId, formData, isValid } = action.payload;

      state.pristine = false;
      state.currentSubmissionSlotData =
        state.currentSubmissionSlotData?.map((item) =>
          item.id === tabId
            ? {
                ...item,
                survey: {
                  ...item.survey,
                  data: JSON.parse(JSON.stringify(formData)) as unknown,
                  isValid: isValid,
                },
              }
            : item
        ) ?? null;
    });

    builder.addCase(setStatus, (state, action) => {
      state.status = action.payload;
    });

    builder.addCase(setDataTaskPreviousState, (state, action) => {
      state.dataTaskPreviousState = action.payload;
    });
  }
);

const initializeState = (state: SubmissionSlotDetailsState) => {
  state.currentSubmissionSlotData = null;
  state.enableLoading = false;
  state.pristine = true;
  state.isSubmitVisible = true;
  state.validationStatus = ValidationStatus.READY;
  state.status = undefined;
  state.dataTaskPreviousState = false;
};

export default submissionSlotDetailsReducer;
